import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router";
import {
    X,
    Mail,
    Phone,
    UserCircle2,
    CalendarDays,
    IdCard,
    Clock,
    Copy,
    Briefcase,
    Search,
    FileText,
    User,
    MailCheck,
    PhoneCall,
    MessageSquare,
    Tag,
    ShoppingBag,
    Package,
} from "lucide-react";
import { useApiCall } from "../../../component/custom-hooks/api-call";
import InnerLoader from "../../../component/ui/loader/inner-loader";
import Chip from "../../../component/ui/chip";
import { Alert, Snackbar } from "@mui/material";
import DataNotFound from "../../../component/ui/loader/data-not-found";
import Skipped from "../../../component/ui/loader/skipped";


const tabs = [
    { key: "scoring", label: "Scoring" },
    { key: "flags", label: "Flags" },
    { key: "remarks", label: "Remarks" },
];


function classNames(...xs) {
    return xs.filter(Boolean).join(" ");
}



function Stat({ title, value, icon: Icon, hint, tone = "default" }) {
    return (
        <div className="group relative rounded-2xl border border-solid border-slate-200 bg-white p-4 shadow-sm hover:shadow-md">
            <div className="flex items-start gap-3">
                <div className="min-w-0">
                    <div className="text-xs text-slate-500 capitalize">
                        {title.includes("_")
                            ? title.split("_").map((word) => ` ${word}`)
                            : title}
                    </div>
                    {/* truncate */}
                    <div className="text-lg font-semibold tracking-tight">{value}</div>
                    {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
                </div>
            </div>
        </div>
    );
}

function Field({
    label,
    value,
    icon: Icon,
    copyable,
    link,
    endTag = false,
    LastElement,
}) {
    const [copied, setCopied] = useState(false);
    return (
        <div className="grid grid-cols-12 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50">
            <div className="col-span-4 flex items-center gap-2 text-[13px] font-medium text-slate-500">
                {Icon ? <Icon className="size-4" /> : null}
                {label}
            </div>
            <div className="col-span-8 flex items-center justify-between gap-2 text-[13px] text-slate-800">
                {link ? (
                    <a
                        href={link}
                        className="truncate underline decoration-slate-300 underline-offset-2 hover:text-blue-600 hover:decoration-blue-400"
                    >
                        {value}
                    </a>
                ) : (
                    <span className="truncate">{value}</span>
                )}
                {copyable || endTag ? (
                    copyable ? (
                        <button
                            onClick={async () => {
                                try {
                                    await navigator.clipboard.writeText(String(value ?? ""));
                                    setCopied(true);
                                    setTimeout(() => setCopied(false), 1500);
                                } catch { }
                            }}
                            className="inline-flex items-center gap-1 rounded-md border border-solid border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100"
                        >
                            <Copy className="size-3.5" /> {copied ? "Copied" : "Copy"}
                        </button>
                    ) : (
                        <LastElement />
                    )
                ) : null}
            </div>
        </div>
    );
}


export default function CallQuickView({
    open = true,
    onClose = () => { },
    id,
    type
}) {
    const [call, setCall] = useState({});
    const [activeTab, setActiveTab] = useState("scoring");
    const [query, setQuery] = useState("");
    const containerRef = useRef(null);
    const apiCall = useApiCall();
    const [isLoading, setIsLoading] = useState(false);
    const [alertData, setAlertData] = useState({ isShow: false });


    const GetData = async (id, type) => {

        let apiData = JSON.stringify({
            id: id,
            type: type
        });
        try {
            let call = await apiCall({
                method: "post",
                url: `view-qa-summary`,
                data: apiData,

            });

            setCall(call?.data);
            setIsLoading(true);
        } catch (error) {
            console.warn("Error : ", error);
        }
    };


    useEffect(() => {
        function onKey(e) {
            if (e.key === "Escape") onClose();
            if (e.key.toLowerCase() === "f" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                const el = document.getElementById("quickview-search");
                el?.focus();
            }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);


    const handleClose = () => {
        setAlertData({ isShow: false });
    };

    const fields = useMemo(
        () => [
            { tab: "asd", label: "How well the agent understood and presented the product", value: call?.scoring?.product_knowledge, icon: IdCard },

        ],
        [call]
    );

    const filteredFields = fields.filter(
        (f) =>
            (!query ||
                `${f.label} ${f.value}`.toLowerCase().includes(query.toLowerCase())) &&
            (activeTab === "scoring"
                ? ["scoring", "flags", "remarks"].includes(f.tab)
                : f.tab === activeTab)
    );

    useEffect(() => {
        GetData(id, type);
    }, [id, type]);




    return (
        <>
            <AnimatePresence>
                {open && (
                    <motion.div
                        className="fixed inset-0 z-[60] flex items-stretch justify-end bg-slate-900/40 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.aside
                            ref={containerRef}
                            initial={{ x: 40, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: 40, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 380, damping: 32 }}
                            className="relative mt-auto mr-[2vh] mb-auto ml-0 flex h-[96vh] w-[800px] max-w-[98vw] flex-col overflow-hidden rounded-3xl border border-solid border-slate-200 bg-gradient-to-b from-white to-slate-50 shadow-xl"
                            aria-modal
                            role="dialog"
                        >
                            {isLoading ? (
                                <>
                                    {/* Header */}
                                    <div className="border-b border-solid border-slate-200 bg-white/90 p-4 backdrop-blur supports-[backdrop-filter]:bg-white/70 w-full">
                                        <div className="flex flex-wrap items-center justify-between gap-3 w-full">
                                            <div className="flex min-w-0 items-center gap-3">
                                                <div className="flex items-center justify-center size-12 bg-slate-200 rounded-full overflow-hidden">
                                                    <p className="capitalize font-semi-bold text-base text-black">
                                                        {call?.basic_details?.customer_name?.split(" ")
                                                            ?.map((n, i) => n.charAt(0))}
                                                    </p>
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <h2 className="truncate text-lg font-semibold tracking-tight text-slate-900">
                                                            {call?.basic_details?.customer_name}
                                                        </h2>
                                                        <Badge
                                                            tone={
                                                                call?.risk_level === "Low"
                                                                    ? "green"
                                                                    : call?.risk_level === "Medium"
                                                                        ? "blue"
                                                                        : "red"
                                                            }
                                                        >
                                                            {call?.risk_level}
                                                        </Badge>
                                                    </div>
                                                    <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                                                        <span className="inline-flex items-center gap-1">
                                                            <MailCheck className="size-4" />{" "}
                                                            {call?.basic_details?.email}
                                                        </span>

                                                        <span className="inline-flex items-center gap-1">
                                                            <PhoneCall className="size-4" />{" "}
                                                            {call?.basic_details?.phone}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex flex-wrap items-center gap-2">
                                                {/* <button
                          className="inline-flex items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                          onClick={() => {
                            if (call?.invoice_details) {
                              setInvoicePopup(true);
                              setInvoiceData(call?.invoice_details);
                            }
                          }}
                          aria-label="Close"
                        >
                          <FileText className="size-4" /> View Invoice
                        </button> */}

                                                <button
                                                    className="inline-flex items-center gap-1.5 rounded-xl bg-[#0B54FF] px-3 py-2 text-sm font-medium text-white hover:bg-[#0849e0]"
                                                    onClick={onClose}
                                                    aria-label="Close"
                                                >
                                                    <X className="size-4" /> Exit
                                                </button>
                                            </div>
                                        </div>

                                        {/* Quick actions */}
                                        <div className="mt-4 flex flex-wrap items-center gap-2">
                                            <QuickAction
                                                icon={Mail}
                                                label="Email"
                                                href={`mailto:${call?.basic_details?.email}`}
                                            />
                                            <QuickAction
                                                icon={Phone}
                                                label="Call"
                                                href={`tel:${call?.basic_details?.phone}`}
                                            />
                                            <div className="ml-auto flex items-center gap-2">
                                                <kbd className="rounded-md border border-solid bg-white px-1.5 py-1 text-[10px] text-slate-500">
                                                    ⌘/Ctrl
                                                </kbd>
                                                <span className="text-[10px] text-slate-500">
                                                    + F to search
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Body */}
                                    <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden p-4 w-full">
                                        {/* Left: main content */}
                                        <div className="col-span-12 flex min-h-0 flex-col gap-4 lg:col-span-12">
                                            {/* Top stats */}
                                            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">

                                                <Stat
                                                    title="Stage"
                                                    value={call?.basic_details?.stage ?? "-"}
                                                    icon={CalendarDays}
                                                />

                                                <Stat
                                                    title="C Type"
                                                    value={call?.basic_details?.c_type ?? "-"}
                                                    icon={CalendarDays}
                                                />

                                                <Stat
                                                    title="Agent"
                                                    value={call?.basic_details?.agent_name ?? "-"}
                                                    icon={CalendarDays}
                                                />

                                                <Stat
                                                    title="Total Score"
                                                    value={call?.total_score ?? "-"}
                                                    icon={CalendarDays}
                                                />
                                            </div>

                                            {/* Tabs */}
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="flex flex-wrap items-center gap-1">
                                                    {tabs?.map((t) => (
                                                        <button
                                                            key={t.key}
                                                            onClick={() => setActiveTab(t.key)}
                                                            className={classNames(
                                                                "rounded-md px-[12px] py-[8px] text-sm font-medium border border-solid border-[#E1E4EA]",
                                                                activeTab === t.key
                                                                    ? "bg-[#F5F5F5] text-[#0E121B]"
                                                                    : "text-slate-600 hover:bg-slate-100"
                                                            )}
                                                        >
                                                            {t.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                {/* <div className="relative">
                                                    <Search className="pointer-events-none absolute left-3 top-2.5 size-4 text-slate-400" />
                                                    <input
                                                        id="quickview-search"
                                                        placeholder="Search fields…"
                                                        value={query}
                                                        onChange={(e) => setQuery(e.target.value)}
                                                        className="w-64 rounded-2xl border border-solid border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none placeholder:text-slate-400 focus:ring-2 focus:ring-slate-900/10"
                                                    />
                                                </div> */}
                                            </div>

                                            <div className="h-full overflow-auto rounded-2xl py-2">
                                                {/* Render fields for current tab (or summary) */}
                                                <div className="grid grid-cols-1 gap-3">
                                                    {filteredFields?.map((f, idx) => (
                                                        <Field key={idx} {...f} />
                                                    ))}
                                                </div>

                                                {activeTab === "scoring" && (
                                                    <div className="grid grid-cols-1 gap-4">
                                                        <ScoringCard
                                                            category="Sales Accuracy"
                                                            questions={[
                                                                {
                                                                    label: "How well the agent understood and presented the product",
                                                                    value: call?.scoring?.product_knowledge,
                                                                    description: call?.scoring?.product_knowledge_description,
                                                                },
                                                                {
                                                                    label: "Was pricing clearly communicated?",
                                                                    value: call?.scoring?.pricing_and_terms_clarity,
                                                                    description: call?.scoring?.pricing_and_terms_clarity_description,
                                                                },
                                                                {
                                                                    label: "Deduct if any false promises or incorrect claims",
                                                                    value: call?.scoring?.misrepresentation,
                                                                    description: call?.scoring?.misrepresentation_description,
                                                                },
                                                            ]}
                                                        />

                                                        <ScoringCard
                                                            category="Customer Experience"
                                                            questions={[
                                                                {
                                                                    label: "Professional and polite tone",
                                                                    value: call?.scoring?.tone_and_courtesy,
                                                                    description: call?.scoring?.tone_and_courtesy_description,
                                                                },
                                                                {
                                                                    label: "Was the agent attentive to customer needs?",
                                                                    value: call?.scoring?.listening_and_empathy,
                                                                    description: call?.scoring?.listening_and_empathy_description,
                                                                },
                                                                {
                                                                    label: "How well did they handle concerns or doubts?",
                                                                    value: call?.scoring?.objection_handling,
                                                                    description: call?.scoring?.objection_handling_description,
                                                                },
                                                            ]}
                                                        />

                                                        <ScoringCard
                                                            category="Compliance"
                                                            questions={[
                                                                {
                                                                    label: "Did they follow internal SOPs (scripts, disclaimers)?",
                                                                    value: call?.scoring?.policy_adherence,
                                                                    description: call?.scoring?.policy_adherence_description,
                                                                },
                                                            ]}
                                                        />

                                                        <ScoringCard
                                                            category="Retention Signals"
                                                            questions={[
                                                                {
                                                                    label: "Was a frustrated client calmed down or escalated?",
                                                                    value: call?.scoring?.dissatisfaction_handled,
                                                                    description: call?.scoring?.dissatisfaction_handled_description,
                                                                },
                                                            ]}
                                                        />

                                                        <ScoringCard
                                                            category="Upsell Signals"
                                                            questions={[
                                                                {
                                                                    label: "Did they miss a good moment to upsell or cross-sell?",
                                                                    value: call?.scoring?.missed_opportunity,
                                                                    description: call?.scoring?.missed_opportunity_description,
                                                                },
                                                            ]}
                                                        />

                                                        <ScoringCard
                                                            category="Overall Flow"
                                                            questions={[
                                                                {
                                                                    label: "Structure, pacing, and professionalism",
                                                                    value: call?.scoring?.flow,
                                                                    description: call?.scoring?.flow_description,
                                                                },
                                                            ]}
                                                        />
                                                    </div>
                                                )}

                                                {activeTab === "flags" &&
                                                    (call?.flags?.step3_skipped
                                                        ? <Skipped title={"Step 3 Skipped"} />
                                                        : <FlagsCard flags={call?.flags} />
                                                    )
                                                }

                                                {activeTab === "remarks" && <RemarksCard remarks={call?.remarks} />}


                                            </div>
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div className="flex items-center justify-between gap-2 border-t border-solid border-slate-200 bg-white p-3 w-full">
                                        <div className="flex items-center gap-2 text-xs text-slate-500">
                                            <IdCard className="size-4" /> ID {call?.id}
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <InnerLoader wrapperClass={"w-full"} />
                            )}
                        </motion.aside>
                    </motion.div>
                )}
            </AnimatePresence>



            <Snackbar
                open={alertData?.isShow}
                autoHideDuration={3000}
                onClose={handleClose}
            >
                <Alert
                    onClose={handleClose}
                    severity={alertData?.type}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {alertData?.message}
                </Alert>
            </Snackbar>
        </>
    );
}

function QuickAction({ icon: Icon, label, href, onClick }) {
    const className =
        "inline-flex items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50";
    if (href)
        return (
            <a className={className} href={href}>
                <Icon className="size-4" /> {label}
            </a>
        );
    return (
        <button className={className} onClick={onClick}>
            <Icon className="size-4" /> {label}
        </button>
    );
}

function Badge({ children, tone = "default", icon: Icon }) {
    const tones = {
        default: "bg-slate-100 text-slate-700",
        green: "bg-emerald-100 text-emerald-700",
        blue: "bg-blue-100 text-blue-700",
        amber: "bg-amber-100 text-amber-800",
        red: "bg-rose-100 text-rose-700",
        grey: "bg-neutral-100 text-neutral-700",
    };
    return (
        <span
            className={classNames(
                "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
                tones[tone]
            )}
        >
            {Icon ? <Icon size={14} /> : null}
            {children}
        </span>
    );
}


function ScoringCard({ category, questions }) {
    const [expanded, setExpanded] = useState(true);

    const getTone = (score) => {
        if (score >= 8) return "green";
        if (score >= 4) return "amber";
        return "red";
    };

    return (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md p-4">
            <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpanded(!expanded)}
            >
                <h3 className="text-sm font-semibold text-slate-800">{category}</h3>
                <span className="text-slate-400 text-xs">{expanded ? "−" : "+"}</span>
            </div>
            {expanded && (
                <div className="mt-3 space-y-3">
                    {questions.map((q, i) => (
                        <div key={i} className="flex flex-col gap-1">
                            <div className="flex items-center justify-between">
                                <span className="text-[13px] text-slate-700">{q.label}</span>
                                <span
                                    className={`px-2 py-0.5 rounded text-xs font-medium ${getTone(q.value) === "green"
                                        ? "bg-emerald-100 text-emerald-700"
                                        : getTone(q.value) === "amber"
                                            ? "bg-amber-100 text-amber-700"
                                            : "bg-rose-100 text-rose-700"
                                        }`}
                                >
                                    {q.value ?? "-"}
                                </span>
                            </div>
                            <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
                                <div
                                    className={`h-2 rounded-full ${getTone(q.value) === "green"
                                        ? "bg-emerald-500"
                                        : getTone(q.value) === "amber"
                                            ? "bg-amber-500"
                                            : "bg-rose-500"
                                        }`}
                                    style={{ width: `${q.value ? (q.value / 10) * 100 : 0}%` }}
                                />
                            </div>
                            {q.description && (
                                <p className="text-[12px]">{q.description}</p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const FLAG_OPTIONS = {
    wrong_sale: [
        "Promised unsupported feature",
        "Incorrect product match",
        "Fake urgency / misleading",
        "None of Them",
    ],
    training_needed: ["Product knowledge", "Soft skills", "Compliance", "Upsell skills", "None of Them"],
    refund_trigger: ["Client sounded unhappy", "Dispute warning signs", "False commitment", "None of Them"],
    missed_opportunity: ["Didn't upsell", "Missed renewal", "Didn't cross-sell", "None of Them"],
    production_misalignment: ["Overpromised", "Miscommunication with production", "None of Them"],
};

const FLAG_COLORS = {
    wrong_sale: "bg-rose-100 text-rose-700",
    training_needed: "bg-amber-100 text-amber-700",
    refund_trigger: "bg-blue-100 text-blue-700",
    missed_opportunity: "bg-purple-100 text-purple-700",
    production_misalignment: "bg-green-100 text-green-700",
};

function FlagsCard({ flags }) {
    if (!flags || flags.step3_skipped) return null; // skip if step skipped

    // Build an array of flags dynamically
    const flagItems = Object.keys(flags)
        .filter((key) => key.startsWith("flag_") && !key.endsWith("_description"))
        .map((key) => ({
            key,
            label: key.replace("flag_", "").replace(/_/g, " "), // readable label
            value: flags[key]
                ?.split(",")
                .map((v) => v.trim().toLowerCase()), // user's selected options
            description: flags[`${key}_description`] || "",
        }));

    return (
        <div className="grid grid-cols-1 gap-4">
            {flagItems.map((f, idx) => {
                const typeKey = f.key.replace("flag_", "");
                const options = FLAG_OPTIONS[typeKey] || [];

                return (
                    <div
                        key={idx}
                        className="rounded-2xl border border-slate-200 bg-white shadow-md hover:shadow-lg transition p-4"
                    >
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold text-slate-800">{f.label}</h4>
                        </div>

                        {/* Show all options, highlight user's selection */}
                        <div className="flex flex-wrap gap-2 mb-2">
                            {options.map((opt, i) => {
                                const isSelected = f.value?.includes(opt.toLowerCase());
                                return (
                                    <span
                                        key={i}
                                        className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${isSelected
                                            ? FLAG_COLORS[typeKey] || "bg-slate-200 text-slate-700"
                                            : "bg-slate-100 text-slate-500"
                                            }`}
                                    >
                                        {opt}
                                    </span>
                                );
                            })}
                        </div>

                        {/* Optional description */}
                        {f.description && (
                            <p className="text-[12px]">{f.description}</p>
                        )}
                    </div>
                );
            })}
        </div>
    );
}


function RemarksCard({ remarks }) {
    if (!remarks) return null;

    return (
        <div className="grid grid-cols-1 gap-4">
            {/* Summary */}
            <div className="rounded-2xl border border-slate-200 bg-white shadow-md p-4">
                <h4 className="text-sm font-semibold text-slate-800 mb-1">Summary</h4>
                <p className="text-[13px] text-slate-700">{remarks.summary ?? "-"}</p>
            </div>

            {/* Action */}
            <div className="rounded-2xl border border-slate-200 bg-white shadow-md p-4">
                <h4 className="text-sm font-semibold text-slate-800 mb-1">Action</h4>
                <p className="text-[13px] text-slate-700">{remarks.action ?? "-"}</p>
            </div>

            {/* Timestamp */}
            <div className="rounded-2xl border border-slate-200 bg-white shadow-md p-4">
                <h4 className="text-sm font-semibold text-slate-800 mb-1">Timestamp</h4>
                <p className="text-[13px] text-slate-500">{remarks.timestamp ?? "-"}</p>
            </div>

            {/* Recordings */}
            {remarks.recordings?.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white shadow-md p-4">
                    <h4 className="text-sm font-semibold text-slate-800 mb-2">Recordings</h4>
                    <div className="flex flex-col gap-2">
                        {remarks.recordings.map((rec, i) => (
                            <a
                                key={i}
                                href={rec}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 text-sm hover:underline truncate"
                            >
                                Recording {i + 1}
                            </a>
                        ))}
                    </div>
                </div>
            )}

            {/* Screenshots */}
            {remarks.screenshots?.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white shadow-md p-4">
                    <h4 className="text-sm font-semibold text-slate-800 mb-2">Screenshots</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {remarks.screenshots.map((src, i) => (
                            <a key={i} href={src} target="_blank" rel="noopener noreferrer">
                                <img
                                    src={src}
                                    alt={`Screenshot ${i + 1}`}
                                    className="rounded-lg border border-slate-200 object-cover w-full h-24 hover:scale-105 transition-transform"
                                />
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}